import React, { useState, useEffect, useRef, useCallback } from 'react';
import cogoToast from 'cogo-toast';
import Modal from '../../../components/UI/ModalTailwind';
import AbonarForm from './AbonarForm';
import {
    getInquilinosBalance,
    setAbono,
    getViviendasBySubfraccionamiento,
    getInquilinosPorVivienda
} from '../requests';
import { getMetodosPago } from '../../../helpers/requests';

export default function AbonarGeneralModal({
    isOpen,
    onClose,
    onAbonoSuccess,
    inquilinoPreseleccionado = null
}) {
    const [loading, setLoading] = useState(false);
    const [subfraccionamientos, setSubfraccionamientos] = useState([]);
    const [viviendas, setViviendas] = useState([]);
    const [inquilinos, setInquilinos] = useState([]);
    const [metodosPago, setMetodosPago] = useState([]);

    const [selectedSub, setSelectedSub] = useState('');
    const [selectedViv, setSelectedViv] = useState('');
    const [selectedInquilino, setSelectedInquilino] = useState(null);

    const [loadingAbono, setLoadingAbono] = useState(false);
    const formRef = useRef();

    // Preselección: setea los valores al abrir
    useEffect(() => {
        if (inquilinoPreseleccionado && isOpen) {
            setSelectedSub(String(inquilinoPreseleccionado.id_subfraccionamiento));
            setSelectedViv(String(inquilinoPreseleccionado.id_vivienda));
            setSelectedInquilino({
                id_inquilino: inquilinoPreseleccionado.id_inquilino,
                nombre: inquilinoPreseleccionado.inquilino,
                numero_registro: inquilinoPreseleccionado.numero_registro,
                subfraccionamiento: inquilinoPreseleccionado.subfraccionamiento,
                id_subfraccionamiento: inquilinoPreseleccionado.id_subfraccionamiento,
                id_vivienda: inquilinoPreseleccionado.id_vivienda
            });
        }
    }, [inquilinoPreseleccionado, isOpen]);

    // Resetear al cerrar
    useEffect(() => {
        if (!isOpen) {
            setSelectedSub('');
            setSelectedViv('');
            setSelectedInquilino(null);
            setViviendas([]);
            setInquilinos([]);
        }
    }, [isOpen]);

    // Cargar subfraccionamientos y métodos de pago
    useEffect(() => {
        const loadData = async () => {
            if (!isOpen) return;
            setLoading(true);
            try {
                const balanceRes = await getInquilinosBalance(localStorage.getItem('frac'), 0);
                if (balanceRes.balances) {
                    const subsMap = {};
                    balanceRes.balances.forEach(r => {
                        if (r.id_subfraccionamiento && r.subfraccionamiento) {
                            subsMap[r.id_subfraccionamiento] = r.subfraccionamiento;
                        }
                    });
                    const subs = Object.keys(subsMap).map(id => ({
                        id: Number(id),
                        nombre: subsMap[id]
                    }));
                    setSubfraccionamientos(subs);
                }

                const pagoRes = await getMetodosPago();
                if (pagoRes.metodosPago) {
                    const filtrados = pagoRes.metodosPago.filter(m => m.Id_FormaPago !== -1);
                    setMetodosPago(filtrados);
                }
            } catch (error) {
                cogoToast.error('Error al cargar datos iniciales', { position: 'bottom-right' });
            }
            setLoading(false);
        };
        loadData();
    }, [isOpen]);

    // Cascada: sub → viviendas
    useEffect(() => {
        const fetchViviendas = async () => {
            if (!selectedSub) {
                setViviendas([]);
                return;
            }
            setLoading(true);
            const response = await getViviendasBySubfraccionamiento(
                localStorage.getItem('frac'),
                Number(selectedSub)
            );
            if (response.error) {
                cogoToast.error(response.error, { position: 'bottom-right' });
                setViviendas([]);
            } else {
                setViviendas(response.viviendas || []);
            }
            setLoading(false);
        };
        fetchViviendas();
    }, [selectedSub]);

    // Cascada: vivienda → inquilinos
    useEffect(() => {
        const fetchInquilinos = async () => {
            if (!selectedViv) {
                setInquilinos([]);
                return;
            }
            setLoading(true);
            const response = await getInquilinosPorVivienda(
                Number(selectedViv),
                localStorage.getItem('frac')
            );
            if (response.error) {
                cogoToast.error(response.error, { position: 'bottom-right' });
                setInquilinos([]);
            } else {
                setInquilinos(response.inquilinos || []);
            }
            setLoading(false);
        };
        fetchInquilinos();
    }, [selectedViv]);

    const handleAbonoSubmit = useCallback(async (formData) => {
        setLoadingAbono(true);
        const response = await setAbono(formData);
        setLoadingAbono(false);
        if (response.error) {
            cogoToast.error(response.error, { position: 'bottom-right' });
            return;
        }
        if (response.created) {
            cogoToast.success('Movimiento realizado', { position: 'bottom-right' });
            onClose();
            if (onAbonoSuccess) onAbonoSuccess();
        }
    }, [onClose, onAbonoSuccess]);

    const handleSubmit = () => {
        if (formRef.current && typeof formRef.current.submit === 'function') {
            formRef.current.submit();
        } else {
            cogoToast.error('Error al enviar el formulario', { position: 'bottom-right' });
        }
    };

    const subNombre = subfraccionamientos.find(s => s.id === Number(selectedSub))?.nombre || '';
    const viviendaNumero = viviendas.find(v => v.id_vivienda === Number(selectedViv))?.numero_registro || '';
    const inquilinoNombre = selectedInquilino?.nombre || selectedInquilino?.inquilino || '';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Nuevo Abono"
            size="md"
            submitLabel="ABONAR"
            showSubmit={!!selectedInquilino}
            loading={loading || loadingAbono}
            disabled={!selectedInquilino}
            onSubmit={handleSubmit}
        >
            <div className="tw-space-y-4">
                {/* Selectores en cascada siempre visibles */}
                <div className="tw-space-y-4">
                    <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                            Subfraccionamiento
                        </label>
                        <select
                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-border-transparent disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                            value={selectedSub}
                            onChange={e => setSelectedSub(e.target.value ? Number(e.target.value) : '')}
                            disabled={loading}
                        >
                            <option value="">Todos</option>
                            {subfraccionamientos.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                            Vivienda
                        </label>
                        <select
                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-border-transparent disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                            value={selectedViv}
                            onChange={e => setSelectedViv(e.target.value)}
                            disabled={!selectedSub || loading}
                        >
                            <option value="">Seleccione</option>
                            {viviendas.map(v => (
                                <option key={v.id_vivienda} value={v.id_vivienda}>
                                    {v.numero_registro}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                            Residente
                        </label>
                        <select
                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-border-transparent disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                            value={selectedInquilino?.id_inquilino || ''}
                            onChange={e => {
                                const id = parseInt(e.target.value);
                                const inquilino = inquilinos.find(r => r.id_inquilino === id);
                                setSelectedInquilino(inquilino || null);
                            }}
                            disabled={!selectedViv || loading}
                        >
                            <option value="">Seleccione</option>
                            {inquilinos.map(r => (
                                <option key={r.id_inquilino} value={r.id_inquilino}>
                                    {r.nombre || r.inquilino}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <hr className="tw-border-t tw-border-gray-200 tw-my-4" />

                {/* Formulario de abono */}
                <AbonarForm
                    ref={formRef}
                    numero_registro={viviendaNumero}
                    subfraccionamiento={subNombre}
                    inquilino={inquilinoNombre}
                    id_inquilino={selectedInquilino?.id_inquilino || 0}
                    metodosPago={metodosPago}
                    loadingInquilinos={loadingAbono}
                    onSubmit={handleAbonoSubmit}
                    simple={true}
                />
            </div>
        </Modal>
    );
}